import {
    AutomationRule,
    AutomationWorker,
    AutomationWorkerId,
    CatalogUsage,
    ConditionKind,
    createMatchUid,
    stripMatchUids,
    PaymentMatchValue,
    ReconsiderationDocument,
    RuleActionType,
    RuleMatch,
    UsageBadgePresentation,
    WorkerDefaultScope,
} from './worker-automation.types';
import { normalizePayerAdjustmentGroup } from '../../dictionary/payer-adjustment-groups';
import { getCarcPairRequirements } from './worker-automation-carc-pair';
import { checkWorkerFunctionForbiddenPatterns, checkWorkerFunctionSyntax } from './worker-function-prompt';
import {
    canExcludeScope,
    createEmptyMatch,
    formatRuleMatchSummary,
    getAvailableMatchScopes,
    getConditionKindsForScope,
    getConditionValues,
    getMatchBlockValidationError,
} from './worker-automation-scope.registry';

export {
    conditionMetaForScope as conditionMeta,
    createEmptyMatch,
    formatMatchBlockSummary,
    formatRuleMatchSummary,
    getAvailableMatchScopes,
    getConditionKindsForScope as getConditionKinds,
    getConditionValues,
    getMatchBlockValidationError,
    getScopeLabel,
} from './worker-automation-scope.registry';

export interface ActionMeta {
    bucket: string;
    label: string;
}

export const RULE_ACTION_SELECT_LABELS: Record<RuleActionType, string> = {
    CLOSE_OUT: 'Close Out',
    SWITCH_TO_PRIVATE_PAY: 'Convert to Private Pay',
    CREATE_APPEAL: 'Reconsideration',
    CREATE_MESSAGE: 'Create Task',
    EXECUTE_ENTER_FUNCTION: 'Execute Function',
};

/** Rule actions available for a given automation worker. Reconsideration is denial-only. */
export function getAvailableRuleActionTypes(workerId: AutomationWorkerId): RuleActionType[] {
    const actions: RuleActionType[] = ['CLOSE_OUT', 'SWITCH_TO_PRIVATE_PAY'];

    if (workerId === 'denial') {
        actions.push('CREATE_APPEAL');
    }

    actions.push('CREATE_MESSAGE', 'EXECUTE_ENTER_FUNCTION');

    return actions;
}

export function getActionMeta(type: string): ActionMeta {
    switch (type) {
        case 'CLOSE_OUT':
            return { label: 'Close Out', bucket: 'Bucket 1' };
        case 'SWITCH_TO_PRIVATE_PAY':
            return { label: 'Convert to Private Pay', bucket: 'Bucket 2' };
        case 'CREATE_APPEAL':
            return { label: 'Reconsideration', bucket: 'Bucket 3' };
        case 'CREATE_MESSAGE':
            return { label: 'Create Task', bucket: 'Bucket 4' };
        case 'EXECUTE_ENTER_FUNCTION':
            return { label: 'Execute Function', bucket: 'Bucket 5' };
        default:
            return { label: type, bucket: '' };
    }
}

export function getDefaultActionValue(type: string): Record<string, unknown> {
    switch (type) {
        case 'CLOSE_OUT':
            return { closeoutDescription: '' };
        case 'SWITCH_TO_PRIVATE_PAY':
            return { memo: '' };
        case 'CREATE_APPEAL':
            return { documents: [], prompt: '' };
        case 'CREATE_MESSAGE':
            return {
                assigned: [],
                messageCategory: 'PAYER_FOLLOWUP',
                messageOwner: 'ENTER',
                suggestedResolution: '',
            };
        case 'EXECUTE_ENTER_FUNCTION':
            // `disableCodingRules` empty = every coder rule stays enabled — a rule added to the
            // account later is never silently disabled by an older EXECUTE_ENTER_FUNCTION rule.
            return { function: '', disableCodingRules: [] };
        default:
            return {};
    }
}

export const ENTER_TASK_FEE_DISCLAIMER = 'Additional fees may apply.';

export function isEnterTaskMessageOwner(messageOwner: unknown): boolean {
    return messageOwner !== 'PROVIDER';
}

export function stripEnterTaskFeeDisclaimer(text: string): string {
    const trimmed = text.trimEnd();

    if (trimmed.endsWith(ENTER_TASK_FEE_DISCLAIMER)) {
        return trimmed.slice(0, -ENTER_TASK_FEE_DISCLAIMER.length).replace(/\s+$/, '');
    }

    return trimmed;
}

export function buildCreateMessageFirstComment(suggestedResolution: string, messageOwner: unknown): string {
    const body = stripEnterTaskFeeDisclaimer(suggestedResolution).trim();

    if (!isEnterTaskMessageOwner(messageOwner)) {
        return body;
    }

    if (!body) {
        return ENTER_TASK_FEE_DISCLAIMER;
    }

    return `${body}\n\n${ENTER_TASK_FEE_DISCLAIMER}`;
}

function extractReconsiderationDocumentIds(value: Record<string, unknown>): string[] {
    const documentIds = value.documentIds;
    if (Array.isArray(documentIds)) {
        return documentIds
            .map(id => (typeof id === 'string' ? id.trim() : ''))
            .filter(Boolean);
    }

    const documents = value.documents;
    if (!Array.isArray(documents)) {
        return [];
    }

    return documents
        .map(doc => {
            if (typeof doc === 'string') {
                return doc.trim();
            }

            if (doc && typeof doc === 'object' && 'documentId' in doc) {
                return String((doc as ReconsiderationDocument).documentId).trim();
            }

            return '';
        })
        .filter(Boolean);
}

function extractReconsiderationDocuments(value: Record<string, unknown>): ReconsiderationDocument[] {
    const documents = value.documents;
    if (Array.isArray(documents) && documents.length > 0) {
        return documents
            .map(doc => {
                if (typeof doc === 'string') {
                    const documentId = doc.trim();
                    return documentId ? { documentId, name: documentId } : null;
                }

                if (doc && typeof doc === 'object' && 'documentId' in doc) {
                    const documentId = String((doc as ReconsiderationDocument).documentId).trim();
                    if (!documentId) {
                        return null;
                    }

                    const name = String((doc as ReconsiderationDocument).name ?? '').trim();
                    return { documentId, name: name || documentId };
                }

                return null;
            })
            .filter((doc): doc is ReconsiderationDocument => doc !== null);
    }

    return extractReconsiderationDocumentIds(value).map(documentId => ({
        documentId,
        name: documentId,
    }));
}

function isCreateAppealAction(type: string): boolean {
    // Legacy configs may still store CREATE_RECONSIDERATION; treat as CREATE_APPEAL.
    return type === 'CREATE_APPEAL' || type === 'CREATE_RECONSIDERATION';
}

const KNOWN_RULE_ACTION_TYPES: ReadonlySet<string> = new Set([
    'CLOSE_OUT',
    'SWITCH_TO_PRIVATE_PAY',
    'CREATE_APPEAL',
    'CREATE_MESSAGE',
    'EXECUTE_ENTER_FUNCTION',
]);

/** Maps legacy / unknown API action types onto the UI RuleActionType set. */
export function resolveRuleActionType(type: string | null | undefined): RuleActionType | null {
    if (!type) {
        return null;
    }

    if (isCreateAppealAction(type)) {
        return 'CREATE_APPEAL';
    }

    if (KNOWN_RULE_ACTION_TYPES.has(type)) {
        return type as RuleActionType;
    }

    return null;
}

export function normalizeReconsiderationRuleForEdit(rule: AutomationRule): AutomationRule {
    if (!isCreateAppealAction(rule.action.type)) {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;
    const { documentIds: _documentIds, documents: _documents, ...rest } = value;

    return {
        ...rule,
        action: {
            ...rule.action,
            type: 'CREATE_APPEAL',
            value: {
                ...rest,
                documents: extractReconsiderationDocuments(value),
            },
        },
    };
}

export function normalizeReconsiderationRuleForPersist(rule: AutomationRule): AutomationRule {
    if (!isCreateAppealAction(rule.action.type)) {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;
    const { documentIds: _documentIds, documents: _documents, ...rest } = value;

    return {
        ...rule,
        action: {
            ...rule.action,
            type: 'CREATE_APPEAL',
            value: {
                ...rest,
                documentIds: extractReconsiderationDocumentIds(value),
            },
        },
    };
}

export function normalizeCreateMessageRuleForEdit(rule: AutomationRule): AutomationRule {
    if (rule.action.type !== 'CREATE_MESSAGE') {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;
    const suggestedResolution = value.suggestedResolution;

    if (typeof suggestedResolution !== 'string' || !suggestedResolution) {
        return rule;
    }

    return {
        ...rule,
        action: {
            ...rule.action,
            value: {
                ...value,
                suggestedResolution: stripEnterTaskFeeDisclaimer(suggestedResolution),
            },
        },
    };
}

export function normalizeCreateMessageRuleForPersist(rule: AutomationRule): AutomationRule {
    if (rule.action.type !== 'CREATE_MESSAGE') {
        return rule;
    }

    const value = rule.action.value as Record<string, unknown>;

    return {
        ...rule,
        action: {
            ...rule.action,
            value: {
                ...value,
                suggestedResolution: buildCreateMessageFirstComment(
                    String(value.suggestedResolution ?? ''),
                    value.messageOwner,
                ),
            },
        },
    };
}

function normalizePaymentMatchCarcGroups(match: RuleMatch): RuleMatch {
    if (match.scope !== 'PAYMENT' || !('carc' in match.value)) {
        return match;
    }

    const payment = match.value as PaymentMatchValue;

    return {
        ...match,
        value: {
            ...payment,
            carc: {
                ...payment.carc,
                group: payment.carc.group.map(group => normalizePayerAdjustmentGroup(group)),
            },
        },
    };
}

/**
 * Removes an `exclude` that has no business being there: any value other than `true`, and
 * any flag sitting on a scope that cannot exclude.
 *
 * This runs on BOTH the edit and persist paths, which is the point — a stale flag in stored
 * config cannot survive a load, and `false` is never written back, so a backend that echoes
 * the config without the key still compares equal to the draft after a save.
 */
function sanitizeMatchExclude(match: RuleMatch): RuleMatch {
    if (match.exclude === undefined) {
        return match;
    }

    if (match.exclude === true && canExcludeScope(match.scope)) {
        return match;
    }

    const { exclude: _dropped, ...rest } = match;

    return rest;
}

function normalizeAutomationRuleMatchValues(rule: AutomationRule): AutomationRule {
    return {
        ...rule,
        match: rule.match.map(match => sanitizeMatchExclude(normalizePaymentMatchCarcGroups(match))),
    };
}

function normalizeRuleActionType(rule: AutomationRule): AutomationRule {
    const resolved = resolveRuleActionType(rule.action?.type);
    if (!resolved || resolved === rule.action.type) {
        return rule;
    }

    return {
        ...rule,
        action: {
            ...rule.action,
            type: resolved,
            value: rule.action?.value ?? getDefaultActionValue(resolved),
        },
    };
}

export function normalizeAutomationRuleForEdit(rule: AutomationRule): AutomationRule {
    const normalized = normalizeAutomationRuleMatchValues(
        normalizeReconsiderationRuleForEdit(
            normalizeCreateMessageRuleForEdit(normalizeRuleActionType(rule)),
        ),
    );

    return {
        ...normalized,
        group: normalized.group === true,
        // Identity for the blocks that came back from the API. The draft and the saved snapshot are
        // both cloned from this result, so an untouched block carries the same uid in both.
        match: (normalized.match ?? []).map(match => (match.uid ? match : { ...match, uid: createMatchUid() })),
        name: normalizeRuleNameForStorage(typeof normalized.name === 'string' ? normalized.name : ''),
    };
}

export function normalizeAutomationRuleForPersist(rule: AutomationRule): AutomationRule {
    const normalized = normalizeAutomationRuleMatchValues(
        normalizeReconsiderationRuleForPersist(normalizeCreateMessageRuleForPersist(rule)),
    );

    return {
        ...normalized,
        // `uid` is ours, not the wire format's — it never leaves the browser.
        match: stripMatchUids(normalized.match),
        name: normalizeRuleNameForStorage(typeof normalized.name === 'string' ? normalized.name : ''),
    };
}

export function createDefaultMatch(workerScope: WorkerDefaultScope) {
    return [createEmptyMatch(workerScope)];
}

function createRuleId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `rule-${crypto.randomUUID()}`;
    }

    return `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const RULE_NAME_MAX_LENGTH = 45;

export function normalizeRuleNameForCompare(name: string): string {
    return name.trim().toLowerCase();
}

/** Length-only clamp — safe for live typing, keeps spaces the user is still typing. */
export function clampRuleName(name: string): string {
    return name.slice(0, RULE_NAME_MAX_LENGTH);
}

/**
 * Stored form of a rule name. Trimmed so the persisted value matches the trim-based
 * validation and uniqueness semantics; the trailing trim covers a clamp landing on a space.
 */
export function normalizeRuleNameForStorage(name: string): string {
    return clampRuleName(name.trim()).trimEnd();
}

/** Case-insensitive uniqueness across denial and rejection rules. */
export function isRuleNameUniqueAcrossWorkers(
    workers: Record<AutomationWorkerId, AutomationWorker>,
    name: string,
    excludeRuleId?: string,
): boolean {
    const normalized = normalizeRuleNameForCompare(name);
    if (!normalized) {
        return true;
    }

    for (const worker of Object.values(workers)) {
        for (const rule of worker.rules) {
            if (excludeRuleId && rule.id === excludeRuleId) {
                continue;
            }
            if (normalizeRuleNameForCompare(rule.name) === normalized) {
                return false;
            }
        }
    }

    return true;
}

export function getRuleNameValidationError(
    workers: Record<AutomationWorkerId, AutomationWorker>,
    name: string,
    excludeRuleId?: string,
): string | null {
    const trimmed = name.trim();
    if (!trimmed) {
        return 'Rule name is required.';
    }
    if (trimmed.length > RULE_NAME_MAX_LENGTH) {
        return `Rule name must be ${RULE_NAME_MAX_LENGTH} characters or fewer.`;
    }
    if (!isRuleNameUniqueAcrossWorkers(workers, trimmed, excludeRuleId)) {
        return 'Rule name must be unique across denial and rejection rules.';
    }
    return null;
}

/** Coder rule numbers out of a raw action value's `disableCodingRules` — de-duped, sorted, non-numeric dropped. */
export function normalizeDisabledCodingRules(value: unknown): number[] {
    if (!Array.isArray(value)) {
        return [];
    }

    // `Number(null)` is 0 and `Number('')` is 0 — both would silently become "coder rule #0" if
    // coerced blindly, so only a real number or a non-blank numeric string is accepted.
    const numbers = value
        .filter((entry): entry is number | string => typeof entry === 'number' || typeof entry === 'string')
        .map(entry => (typeof entry === 'string' ? entry.trim() : String(entry)))
        .filter(entry => entry !== '')
        .map(Number)
        .filter(Number.isFinite);

    return [...new Set(numbers)].sort((left, right) => left - right);
}

function getExecuteFunctionValidationError(value: Record<string, unknown>): string | null {
    const fn = value.function;
    if (typeof fn !== 'string' || !fn.trim()) {
        return 'Add the JavaScript this rule should run on the claim.';
    }

    // The editor's own syntax check (`ConfigurationFunctionEditor`) is display-only — it does not
    // gate Save. Without this, a manual edit left mid-syntax-error (unlike AI output, which never
    // reaches the rule until it already parses) can be persisted and only fail once the backend
    // tries to run it against a real claim. Re-running the same check here is what actually blocks it.
    const syntaxError = checkWorkerFunctionSyntax(fn);
    if (syntaxError) {
        return `Fix the syntax error in the function: ${syntaxError}`;
    }

    // A best-effort static check, not a sandbox (see `checkWorkerFunctionForbiddenPatterns`'s own
    // doc comment) — it catches a direct, unobfuscated `fetch`/`eval`/etc. left in by hand or by
    // the AI, but nothing here defends against deliberate evasion. Real enforcement is backend work.
    const forbiddenPatternError = checkWorkerFunctionForbiddenPatterns(fn);
    if (forbiddenPatternError) {
        return forbiddenPatternError;
    }

    return null;
}

/**
 * Action-value errors — a second axis from match-block validation (`getAutomationMatchValidationError`):
 * WHAT the rule does, not WHAT it fires on. Today only EXECUTE_ENTER_FUNCTION has a required action
 * field; other actions have none (an empty closeout description has always been allowed to save).
 */
export function getAutomationActionValidationError(
    workers: Record<AutomationWorkerId, AutomationWorker>,
): string | null {
    for (const worker of Object.values(workers)) {
        for (const rule of worker.rules) {
            const error = getRuleActionValidationError(rule);
            if (error) {
                const name = rule.name.trim() || 'Untitled rule';
                return `${name}: ${error}`;
            }
        }
    }
    return null;
}

export function getRuleActionValidationError(rule: AutomationRule): string | null {
    if (resolveRuleActionType(rule.action?.type) !== 'EXECUTE_ENTER_FUNCTION') {
        return null;
    }
    return getExecuteFunctionValidationError(rule.action.value ?? {});
}

export function getAutomationMatchValidationError(
    workers: Record<AutomationWorkerId, AutomationWorker>,
    savedWorkers: Record<AutomationWorkerId, AutomationWorker>,
): string | null {
    for (const worker of Object.values(workers)) {
        const savedRules = savedWorkers[worker.id]?.rules;
        for (const rule of worker.rules) {
            const carcPairRequired = getCarcPairRequirements(rule.id, rule.match, savedRules);
            for (let index = 0; index < rule.match.length; index++) {
                const error = getMatchBlockValidationError(rule.match[index], {
                    requireCarcPair: carcPairRequired[index],
                });
                if (error) {
                    const name = rule.name.trim() || 'Untitled rule';
                    return `${name}: ${error}`;
                }
            }
        }
    }
    return null;
}

export function createUniqueDefaultRuleName(
    workers: Record<AutomationWorkerId, AutomationWorker>,
): string {
    const base = 'New rule';
    if (isRuleNameUniqueAcrossWorkers(workers, base)) {
        return base;
    }

    for (let index = 2; index < 10_000; index += 1) {
        const candidate = `${base} ${index}`;
        if (candidate.length > RULE_NAME_MAX_LENGTH) {
            break;
        }
        if (isRuleNameUniqueAcrossWorkers(workers, candidate)) {
            return candidate;
        }
    }

    return clampRuleName(`New rule ${Date.now()}`);
}

export function createEmptyRule(
    workerScope: WorkerDefaultScope,
    name = 'New rule',
): AutomationRule {
    return {
        id: createRuleId(),
        name: normalizeRuleNameForStorage(name),
        match: createDefaultMatch(workerScope),
        group: false,
        action: { type: 'CLOSE_OUT', value: getDefaultActionValue('CLOSE_OUT') },
    };
}

/** @deprecated Use formatRuleMatchSummary(rule.match) */
export function formatMatchSummary(rule: AutomationRule, _workerScope: WorkerDefaultScope): string {
    return formatRuleMatchSummary(rule.match);
}

export function getWorkerById(
    workers: Record<AutomationWorkerId, AutomationWorker>,
    id: AutomationWorkerId,
): AutomationWorker {
    return workers[id];
}

export function collectCatalogValuesFromMatch(
    match: AutomationRule['match'][number],
): Array<{ kind: ConditionKind; value: string }> {
    return getConditionKindsForScope(match.scope).flatMap((kind) =>
        getConditionValues(match, kind).map((value) => ({ kind, value })),
    );
}

export function getCatalogValueUsage(
    worker: AutomationWorker,
    excludeRuleId: string,
    kind: ConditionKind,
    value: string,
): CatalogUsage {
    const ruleNames: string[] = [];
    const ruleNumbers: number[] = [];

    worker.rules.forEach((rule, index) => {
        if (rule.id === excludeRuleId) {
            return;
        }

        const used = rule.match.some(
            match =>
                getConditionKindsForScope(match.scope).includes(kind) &&
                getConditionValues(match, kind).some(entry => String(entry) === String(value)),
        );

        if (used) {
            ruleNames.push(rule.name);
            ruleNumbers.push(index + 1);
        }
    });

    return { count: ruleNames.length, ruleNames, ruleNumbers };
}

export function getUsageBadgePresentation(usage: CatalogUsage): UsageBadgePresentation {
    if (usage.count === 0) {
        return {
            used: false,
            notUsed: true,
            label: 'Not yet used',
            short: '',
            title: '',
        };
    }

    const uniq = [...new Set(usage.ruleNumbers)].sort((left, right) => left - right);
    const short = `#${uniq.join(', #')}`;
    const label = uniq.length === 1 ? `Used in rule ${short}` : `Used in rules ${short}`;

    return {
        used: true,
        notUsed: false,
        label,
        short,
        title: label,
    };
}

/** @deprecated Use getUsageBadgePresentation(usage).label */
export function formatUsageLabel(usage: CatalogUsage): string {
    return getUsageBadgePresentation(usage).label;
}
